<template>
  <div class="container">
    <LoadingSpinner v-if="$apollo.loading" />
    <div v-if="errorMessage">{{ errorMessage }}</div>
    <div v-else>
      <EmptyMessage v-if="wordTokenList.length === 0">
        Select a word to load its morphology and other tags
      </EmptyMessage>
      <div v-else class="word-info-content">
        <div
          class="token"
          v-for="wordData in wordTokenList"
          :key="wordData.ref"
          :data-macula-id="wordData.maculaId"
        >
          <div class="summary">
            <div class="lemma" :class="{ rtl: isHebrew(wordData.lemma) }">{{ wordData.lemma }}</div>
            <div class="gloss">{{ getEngGloss(wordData) }}</div>
            <div class="morph" v-for="(summaryLine, index) in summary(wordData)" :key="index">
              {{ summaryLine }}
            </div>
          </div>
          <div class="extra" v-if="wordData?.ln">
            Louw & Nida:
            <a
              v-if="SHOW_ALIGNED_WORD_QUERIES_WORKSPACE"
              :href="getAlignedWordQueriesLink(wordData.ln)"
              target="_blank"
              >{{ wordData.ln }}</a
            >
            <template v-else>{{ wordData.ln }}</template>
          </div>
          <div class="extra" v-if="wordData.senses.length > 0">
            <template v-for="sense in wordData.senses" :key="`${lang}-${sense.uri}`">
              <div :class="{ 'not-localized': sense.isFallback }">
                <span class="dfe">{{ sense.definition }}</span
                >&nbsp;--&nbsp;<span class="gle">{{ sense.glosses.join(', ') }}</span>
                <!-- TODO: Read more / another pane for comments? -->
                <span v-if="SHOW_SENSE_DATA_COMMENTS && sense.comment" class="com">{{
                  sense.comment
                }}</span>
              </div>
              <CopyButton
                class="copy-button"
                v-if="SHOW_CONCEPT_COPY_BUTTON"
                :text="sense.uri"
                @copy-button-click="copySense(sense)"
              />
            </template>
          </div>
        </div>
        <AttributionFooter
          v-if="wordTokenList.some(wordData => wordData.senses.length > 0)"
          :lexicon-data="lexiconData"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import mixins from '@/mixins';
  import {
    DEFAULT_GROUP_KEY,
    MACULA_HEBREW_IMPLIED_ARTICLE,
    SHOW_CONCEPT_COPY_BUTTON,
    MACULA_DEFAULT_GLOSS,
    SHOW_ALIGNED_WORD_QUERIES_WORKSPACE,
    DEFAULT_ALIGNED_TRANSLATION,
    DEFAULT_OSIS_REF,
  } from '@/store/constants';
  import CopyButton from '@/symphony/widgets/components/CopyButton.vue';
  import lexiconDetailQuery from '@/symphony/shared/queries/lexiconDetail';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import EmptyMessage from '@/symphony/widgets/components/EmptyMessage.vue';
  import AttributionFooter from './AttributionFooter.vue';

  const valueMap = {
    number: {
      singular: 'sg',
      plural: 'pl',
    },
    person: {
      first: '1',
      second: '2',
      third: '3',
    },
  };

  const isGreek = word => {
    const greek = /^[\u0370-\u03FF\u1F00-\u1FFF]+$/;
    return greek.test(word);
  };

  const isHebrew = word => {
    const hebrew = /^[\u0590-\u05FF]+$/;
    return hebrew.test(word);
  };

  const PARTICIPLE_VALUES = new Set(['participle', 'participle active', 'participle passive']);
  const isParticiple = value => PARTICIPLE_VALUES.has(value);

  const getGreekMorphSecondLine = wordData => {
    const { case: case_, voice, mood, tense, number, person, gender, class: class_ } = wordData;

    if (class_ === 'verb') {
      if (isParticiple(wordData.mood)) {
        // participle
        return `${tense} ${voice} ${mood} ${case_} ${gender} ${number}`;
      }

      // non-participle verb
      const personStr = person ? valueMap.person[person] : '';
      const numberStr = number ? valueMap.number[number] : '';
      return `${tense} ${voice} ${mood} ${personStr}${numberStr}`;
    }

    // non-verb
    const numberStr = number ? valueMap.number[number] : '';
    return `${case_} ${gender} ${numberStr}`;
  };

  const getHebrewMorphSecondLine = wordData => {
    const { stem, type: tense, number, person, gender, class: class_ } = wordData;
    if (class_ === 'verb') {
      if (isParticiple(tense)) {
        return `${stem} ${tense} ${gender} ${number}`;
      }
      if (tense === 'infinitive absolute' || tense === 'infinitive construct') {
        return `${stem} ${tense}`;
      }

      // finite verb
      return `${stem} ${tense} ${person} ${gender} ${number}`;
    }

    // non-verbal
    if (person) {
      return `${person} ${gender} ${number}`;
    }
    return ` ${gender} ${number}`;
  };

  const cleanWhitespace = value => value.trim().replace(/ {2+}/g, ' ');

  const getSummary = wordData => {
    if (isGreek(wordData.text)) {
      const partOfSpeech = `${wordData?.type} ${wordData.class}`;
      const morph = getGreekMorphSecondLine(wordData);
      return [partOfSpeech, morph];
    }
    if (isHebrew(wordData.text) || wordData?.maculaId.includes(MACULA_HEBREW_IMPLIED_ARTICLE)) {
      // cj does not strike me as a common abbreviation for conjunction
      const partOfSpeech = wordData?.class === 'cj' ? 'conjunction' : wordData?.class;
      const morph = getHebrewMorphSecondLine(wordData);
      return [partOfSpeech, morph];
    }

    return [`Only Greek and Hebrew word formatting is currently supported.`];
  };

  export default {
    name: 'WordInfoWidget',
    mixins: [mixins.InputDataMixin, mixins.LocalizationMixin],
    data() {
      return {
        wordTokenList: [],
        errorMessage: null,
        SHOW_CONCEPT_COPY_BUTTON,
        // TODO: Determine how we want to treat comments; do not query for them unless needed
        SHOW_SENSE_DATA_COMMENTS: false,
        SHOW_ALIGNED_WORD_QUERIES_WORKSPACE,
        lexiconData: null,
      };
    },
    props: {
      doric: {
        inputs: {
          label: 'Word Info',
          selectedTokenIds: {
            groupKey: DEFAULT_GROUP_KEY,
            value: 'selectedTokenIds',
          },
        },
      },
    },
    computed: {
      parsedSelectedTokenIds() {
        try {
          return JSON.parse(this.selectedTokenIds);
        } catch (e) {
          return [];
        }
      },
    },
    methods: {
      summary(wordData) {
        return getSummary(wordData || {}).map(cleanWhitespace);
      },
      isHebrew(word) {
        return isHebrew(word);
      },
      getEngGloss(wordData) {
        return wordData[MACULA_DEFAULT_GLOSS] ? wordData[MACULA_DEFAULT_GLOSS] : wordData.gloss;
      },
      copySense(sense) {
        const { uri } = sense;
        const copyPayload = uri;
        navigator.clipboard.writeText(copyPayload).then(
          () => {
            /* clipboard successfully set */
            const msg = `Copied URI to clipboard: ${copyPayload}`;
            console.log(`Success: ${msg}`);
            // eslint-disable-next-line no-alert
            alert(msg);
          },
          () => {
            /* clipboard write failed */
            console.error('Failed to copy URI to clipboard.');
          },
        );
      },
      getAlignedWordQueriesLink(entryCode) {
        const osisRef = this.$route.query?.osisRef || DEFAULT_OSIS_REF;
        const { workspace } = this.$route.query;
        const isReferenceWorkspace = workspace === 'reference';
        const translation = isReferenceWorkspace
          ? this.$route.query.translation || DEFAULT_ALIGNED_TRANSLATION
          : undefined;
        const text = isReferenceWorkspace ? '' : undefined;
        const linkQuery = {
          ln: entryCode,
          text,
          workspace: isReferenceWorkspace ? 'aligned-word-query' : 'word-query',
          translation,
          osisRef,
        };
        return `${this.$router.resolve({ name: 'home', query: linkQuery }).href}`;
      },
      localizedSenses(senses) {
        if (!senses) {
          return [];
        }
        return senses.map(sense => {
          const { uri, label, localizations } = sense;
          const definition = this.getLocalizedOrFallback(sense, 'definition');
          const comment = this.getLocalizedOrFallback(sense, 'comment');
          // glosses is an array from the parameterized field, but a string in localizations
          // getLocalizedOrFallback will return the appropriate value; normalize to array
          const glossesValue = this.getLocalizedOrFallback(sense, 'glosses');
          const glosses = Array.isArray(glossesValue)
            ? glossesValue
            : glossesValue
                ?.split(',')
                .map(g => g.trim())
                .filter(Boolean) || [];
          // Check if we're showing fallback content (requested language not available)
          const isFallback = !this.isLocalizedLanguageAvailable(localizations);
          return {
            uri,
            label,
            definition,
            glosses,
            comment,
            localizations,
            isFallback,
          };
        });
      },
    },
    apollo: {
      wordTokens: {
        query: gql`
          query WordTokens($filters: WordTokenFilter, $language: String) {
            paginatedWordTokens(filters: $filters) {
              results {
                data
                senses {
                  uri
                  label
                  definition(language: $language, allowFallback: false)
                  glosses(language: $language, allowFallback: false)
                  comment(language: $language, allowFallback: false)
                  localizations {
                    language
                    definition
                    glosses
                    comment
                  }
                }
                maculaId
              }
            }
          }
        `,
        variables() {
          return {
            filters: {
              id: {
                inList: this.parsedSelectedTokenIds,
              },
            },
            language: this.lang,
          };
        },
        skip() {
          return (
            !Array.isArray(this.parsedSelectedTokenIds) || this.parsedSelectedTokenIds.length === 0
          );
        },
        update(data) {
          this.errorMessage = null;
          const newWordTokens = data.paginatedWordTokens.results.map(t => {
            const { maculaId, senses } = t;
            return {
              ...t.data,
              maculaId,
              senses: this.localizedSenses(senses),
            };
          });
          this.wordTokenList = newWordTokens;
        },
        error(error) {
          this.errorMessage = error.message;
          console.error(error);
        },
      },
      lexicon: {
        query: lexiconDetailQuery,
        variables() {
          return {
            // NOTE: This is hardcoded but will eventually be dynamic
            // based on the retrieved senses
            uri: 'ubs-dgnt',
          };
        },
        skip() {
          return (
            !Array.isArray(this.parsedSelectedTokenIds) || this.parsedSelectedTokenIds.length === 0
          );
        },
        update(data) {
          if (data.lexicons && data.lexicons.length > 0) {
            const [firstLexicon] = data.lexicons;
            this.lexiconData = firstLexicon;
          }
          return data.lexicons;
        },
        error(error) {
          console.error('Error fetching lexicon data:', error);
        },
      },
    },
    components: {
      LoadingSpinner,
      EmptyMessage,
      CopyButton,
      AttributionFooter,
    },
  };
</script>

<style scoped lang="scss">
  .token {
    &:has(+ .token) {
      margin-bottom: 1em;
      padding-bottom: 1em;
      border-bottom: 1px solid #ccc;
    }

    .summary {
      background-color: #eee;
      border: 1px solid #ccc;
      border-radius: 0.5em;
      padding: 0.5em;

      div {
        margin: 0.25em;
      }

      .lemma {
        font-weight: bold;
        font-size: 1.4em;
        font-family: 'Gentium Book Plus';
        direction: 'ltr';

        &.rtl {
          font-family: 'SBLBibLit';
          direction: rtl;
          text-align: left;
          // TODO: review styling;
          // https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight
          font-weight: normal;
        }
      }

      .morph {
        font-size: 0.8em;
        color: #666;
      }
    }

    .extra {
      margin-top: 0.5em;
      margin-left: 0.5em;
    }
  }
  .gle {
    color: green;
  }
  .com {
    margin-top: 1em;
    display: inline-block;
    color: gray;
  }
  .copy-button {
    display: block;
    float: right;
  }
  .not-localized {
    color: gray;
  }
  .word-info-content {
    display: flex;
    flex-direction: column;
  }
</style>
